import * as React from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import Container from 'components/Container';
import Form from 'components/Form';
import Icon from 'components/Icon';
import Row from 'components/Row';
import Stack from 'components/Stack';
import Text from 'components/Text';
import useForm from 'hooks/useForm';
import useTrackEvent from 'hooks/useTrackEvent';
import { PAGE_REGIONS } from 'utils';
import { color, media } from 'styles/utils';

const StyledSection = styled.section`
  padding: 4.5rem 1rem;
`;

const StyledCard = styled(Container)`
  background-color: ${color('darkTeal')};
  color: ${color('white')};
  border-radius: 0.5rem;
  padding: 0 6rem;
  overflow: hidden;
  background-image: radial-gradient(
    min(70vw, 54rem) min(50vw, 40rem) at calc(50% + 15rem) 130%,
    #5195b9,
    transparent 80%
  );

  ${media.max('lg')} {
    padding: 0 2rem;
    background-image: radial-gradient(
      100vw 100vw at 50% 110%,
      #5195b9,
      transparent 80%
    );
  }
`;

const StyledStack = styled(Stack)`
  width: 100%;
  max-width: 38rem;
  ${media.max('lg')} {
    padding: 4rem 0;
    align-items: center;
    text-align: center;
  }
`;

const Circles = styled.div`
  position: absolute;
  right: -15%;
  bottom: -20%;
  height: 100%;
  width: 100%;
  background-image: url('/images/circles.svg');
  background-repeat: no-repeat;

  ${media.max('lg')} {
    right: 0%;
    bottom: -20%;
    background-size: cover;
    background-image: url('/images/circles-mobile.svg');
  }
`;

const StyledRow = styled(Row)`
  position: relative;
`;

const StyledImage = styled(GatsbyImage)`
  filter: drop-shadow(0 1rem 4rem #0004) drop-shadow(0 0.25rem 1rem #0004);
  max-width: 80vw;

  ${media.min('lg')} {
    margin-top: 4rem;
  }
`;

export default function GetTheGuide() {
  const { image } = useStaticQuery(graphql`
    query imageQuery {
      image: file(
        relativePath: { eq: "editorial-graphics/roi-guide-cover.webp" }
      ) {
        childImageSharp {
          gatsbyImageData(height: 452, placeholder: NONE)
        }
      }
    }
  `);

  const { trackEvent } = useTrackEvent();

  const form = useForm({
    onSubmit: (event, { formValues, submitterValue }) =>
      trackEvent('content_downloaded', {
        object: 'content',
        action: 'downloaded',
        pageUrl: window.location.href,
        label: submitterValue,
        location: PAGE_REGIONS.hero,
        content_name: 'Zero to 100 Deploys a Day',
        email: formValues.email,
      }),
  });

  return (
    <StyledSection>
      <StyledCard>
        <Circles />
        <StyledRow align="center" breakpoint="lg">
          <StyledStack align="start" gap="2rem">
            <Text as="h2" color="lightestBlue" variant="f2">
              Go from zero to one hundred deploys a day.
            </Text>
            <Form {...form.getFormProps()}>
              {form.isSubmitted ? (
                <Form.Message>
                  {form.isSubmitFailure &&
                    'Something went wrong. Please try again later.'}
                  {form.isSubmitSuccess &&
                    "Thank you! You'll receive the guide via email shortly ✅"}
                </Form.Message>
              ) : (
                <Form.ControlGroup>
                  <Form.Control
                    aria-label="Enter your work email"
                    autocomplete="email"
                    disabled={form.isBusy}
                    name="email"
                    placeholder="Enter your work email"
                    required
                    type="email"
                  />
                  <Form.Submitter disabled={form.isBusy || form.isSubmitted}>
                    Get the Guide &ensp;
                    <Icon name="arrow-up-right" size="sm" />
                  </Form.Submitter>
                </Form.ControlGroup>
              )}
            </Form>
          </StyledStack>
          <StyledImage
            alt=""
            image={getImage(image)}
            objectFit="contain"
            objectPosition="center bottom"
          />
        </StyledRow>
      </StyledCard>
    </StyledSection>
  );
}
